import { useState, useMemo } from "react";
import { icons } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DynamicIcon } from "./DynamicIcon";

/** Convert PascalCase to kebab-case: "ArrowRight" → "arrow-right" */
function toKebabCase(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
    .toLowerCase();
}

const allIcons = Object.keys(icons).map((pascalName) => ({
  pascal: pascalName,
  kebab: toKebabCase(pascalName),
}));

interface IconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
  colorHex?: string;
}

export function IconPicker({ value, onChange, colorHex }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search) return allIcons.slice(0, 60);
    const q = search.toLowerCase();
    return allIcons.filter((i) => i.kebab.includes(q)).slice(0, 60);
  }, [search]);

  const previewColor = colorHex ? `#${colorHex.replace("#", "")}` : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-start gap-2 h-10">
          <DynamicIcon
            name={value}
            className="h-4 w-4 shrink-0"
            style={previewColor ? { color: previewColor } : undefined}
          />
          <span className="truncate text-sm">{value || "Wybierz ikonę"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <Input
          placeholder="Szukaj ikony..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2"
          autoFocus
        />
        <div className="grid grid-cols-6 gap-1 max-h-56 overflow-y-auto">
          {filtered.map((icon) => {
            const isSelected = icon.kebab === value;
            return (
              <button
                key={icon.pascal}
                type="button"
                className={`flex items-center justify-center p-2 rounded-md hover:bg-accent transition-colors ${
                  isSelected ? "bg-accent ring-1 ring-primary" : ""
                }`}
                title={icon.kebab}
                onClick={() => {
                  onChange(icon.kebab);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <DynamicIcon name={icon.kebab} className="h-5 w-5" />
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="col-span-6 text-sm text-muted-foreground text-center py-4">
              Brak wyników
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
